import { useAuth } from '../../auth/use-auth';

/**
 * AuthStatus debug page. Renders the current AuthProvider state for local
 * verification of the four-state auth flow. Not a production page; intended
 * for development and smoke testing during Stream 0 deliverables.
 *
 * Accessible at /test/auth-status when registered in AppRouter. Stream 0
 * may keep this mounted under a dev-only flag; production should not expose
 * it publicly.
 *
 * Updated for Deliverable 7.1: AuthContextValue now exposes `auth` (the
 * discriminated AuthStatus) instead of `state`. The status values are
 * unchanged: loading, unauthenticated, unprovisioned, ready.
 */
export function AuthStatus() {
  const { auth, signOut } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="bdse-glass-card w-[560px] max-w-[92vw] px-8 py-6">
        <h1 className="text-lg font-bold text-bdse-text mb-1">
          Auth Status Debug
        </h1>
        <p className="text-xs text-bdse-text-tertiary mb-4">
          Local diagnostic view of the four-state AuthProvider model.
        </p>

        <div className="space-y-3">
          <Row label="Status">
            <span
              className={[
                'inline-block px-2 py-0.5 rounded-full text-xs font-semibold',
                auth.status === 'ready'
                  ? 'bg-bdse-teal-light text-bdse-teal'
                  : auth.status === 'unauthenticated'
                  ? 'bg-black/5 text-bdse-text-secondary'
                  : auth.status === 'unprovisioned'
                  ? 'text-bdse-amber'
                  : 'text-bdse-text-tertiary',
              ].join(' ')}
              style={
                auth.status === 'unprovisioned'
                  ? { background: 'rgba(184,135,58,0.1)' }
                  : undefined
              }
            >
              {auth.status}
            </span>
          </Row>

          {auth.status === 'ready' && (
            <>
              <Row label="User ID">
                <code className="text-xs font-mono text-bdse-text break-all">
                  {auth.user.id}
                </code>
              </Row>
              <Row label="Email">
                <span className="text-xs text-bdse-text">{auth.user.email}</span>
              </Row>
              <Row label="Profile email">
                <span className="text-xs text-bdse-text">{auth.profile.email}</span>
              </Row>
              <Row label="Role">
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-bdse-teal-light text-bdse-teal">
                  {auth.profile.role}
                </span>
              </Row>
              <Row label="Display name">
                <span className="text-xs text-bdse-text">
                  {auth.profile.display_name ?? '(none)'}
                </span>
              </Row>
              <Row label="Active">
                <span className="text-xs text-bdse-text">
                  {String(auth.profile.is_active)}
                </span>
              </Row>
            </>
          )}

          {auth.status === 'unprovisioned' && (
            <>
              <Row label="User ID">
                <code className="text-xs font-mono text-bdse-text break-all">
                  {auth.user.id}
                </code>
              </Row>
              <Row label="Email">
                <span className="text-xs text-bdse-text">{auth.user.email}</span>
              </Row>
              <Row label="Note">
                <span className="text-xs text-bdse-text-secondary">
                  Session valid but no active user_profiles row found.
                </span>
              </Row>
            </>
          )}

          {auth.status === 'loading' && (
            <Row label="Note">
              <span className="text-xs text-bdse-text-secondary">
                Initial Supabase session probe in flight.
              </span>
            </Row>
          )}

          {auth.status === 'unauthenticated' && (
            <Row label="Note">
              <span className="text-xs text-bdse-text-secondary">
                No Supabase session.
              </span>
            </Row>
          )}
        </div>

        {(auth.status === 'ready' || auth.status === 'unprovisioned') && (
          <div className="mt-5 pt-4 border-t border-black/[0.06]">
            <button
              type="button"
              onClick={signOut}
              className="bdse-btn-ghost"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-black/[0.04]">
      <dt className="text-[10px] uppercase tracking-wider font-semibold text-bdse-text-tertiary w-28 shrink-0 pt-0.5">
        {label}
      </dt>
      <dd className="flex-1 min-w-0">{children}</dd>
    </div>
  );
}
